import { useEffect, useState } from 'react'
import { Route, Routes, useNavigate } from 'react-router'
import { ApiError, setUnauthorizedHandler } from '@/api/client'
import { auth } from '@/api/endpoints'
import type { User } from '@/api/types'
import { RealtimeProvider } from '@/hooks/useEventStream'
import { WorldRefreshProvider, useWorld } from '@/hooks/useWorld'
import { TooltipProvider } from '@/components/ui/tooltip'
import { ScrollArea } from '@/components/ui/scroll-area'
import { ExpertProvider } from '@/components/Expert'
import { NavDrawer } from '@/components/layout/NavDrawer'
import { Rail } from '@/components/layout/Rail'
import { Sidebar } from '@/components/layout/Sidebar'
import { Topbar } from '@/components/layout/Topbar'
import { SessionProvider } from '@/components/Session'
import { Err } from '@/components/Err'
import { Button } from '@/components/ui/button'
import Login from '@/views/Login'
import Signup from '@/views/Signup'
import ResetRequest from '@/views/auth/ResetRequest'
import ResetConfirm from '@/views/auth/ResetConfirm'
import VerifyEmail from '@/views/auth/VerifyEmail'
import { PasswordGate } from '@/views/auth/PasswordGate'
import { Screens } from '@/views/routes'
import { TopSearch } from '@/views/TopSearch'
import { UserShell } from '@/views/user/UserShell'


export default function App() {
  const [user, setUser] = useState<User | null>(null)
  const [sessionChecked, setSessionChecked] = useState(false)
  const [sessionError, setSessionError] = useState<unknown>(null)
  const [sessionVersion, setSessionVersion] = useState(0)
  const nav = useNavigate()

  // 401 은 한 곳에서만 처리한다 — 세션을 버리고 로그인으로 돌린다.
  useEffect(() => {
    setUnauthorizedHandler(() => setUser(null))
    return () => setUnauthorizedHandler(null)
  }, [])

  useEffect(() => {
    let alive = true
    void auth.me()
      .then((current) => {
        if (alive) setUser(current)
      })
      .catch((error: unknown) => {
        if (!alive) return
        setUser(null)
        if (!(error instanceof ApiError && error.status === 401)) setSessionError(error)
      })
      .finally(() => {
        if (alive) setSessionChecked(true)
      })
    return () => {
      alive = false
    }
  }, [sessionVersion])

  if (!sessionChecked) return <SessionWait />
  if (sessionError) {
    return (
      <div className="grid h-full place-items-center">
        <div className="grid w-full max-w-72 gap-3 px-4">
          <Err error={sessionError} />
          <Button
            variant="outline"
            onClick={() => {
              setSessionChecked(false)
              setSessionError(null)
              setSessionVersion((v) => v + 1)
            }}
          >
            다시 확인
          </Button>
        </div>
      </div>
    )
  }

  // 로그인 전의 화면: 로그인, 계정 신청(F-14), 그리고 메일 링크가 여는 셋 — 이메일 확인 · 비밀번호 설정 · 비밀번호 찾기(F-33).
  if (!user) {
    return (
      // 로그인 전에도 같은 UI 어휘를 쓴다 — 계정 신청의 막힘 표시가 Tooltip 이다.
      <TooltipProvider delayDuration={200}>
        <Routes>
          <Route path="/signup" element={<Signup />} />
          <Route path="/verify" element={<VerifyEmail />} />
          <Route path="/reset" element={<ResetRequest />} />
          <Route path="/reset/confirm" element={<ResetConfirm />} />
          <Route path="*" element={<Login onIn={setUser} />} />
        </Routes>
      </TooltipProvider>
    )
  }

  // 비밀번호가 없는 계정(OAuth 로 들어온 사람, F-34)은 셸 대신 설정 화면을 본다 — OAuth 는 편의이고 비밀번호가 자격이다.
  if (user.has_password === false) {
    return (
      <PasswordGate
        username={user.username}
        onSet={() => { auth.me().then(setUser).catch(() => undefined) }}
        onLogout={() => { auth.logout().catch(() => undefined).finally(() => setUser(null)) }}
      />
    )
  }

  const onOut = () => {
    setUser(null)
    nav('/')
  }
  // 역할이 화면을 가른다. 관리자(최고 관리자·관리자)는 운영 화면, 사용자는 내 VM 과 내 팀 — 같은 API 위의 다른 화면이다.
  // 두 관리자의 차이는 셸이 아니라 메뉴와 화면 목록이다(F-38: `components/layout/nav.ts`).
  return (
    <TooltipProvider delayDuration={200}>
      <SessionProvider user={user} refresh={() => { auth.me().then(setUser).catch(() => undefined) }}>
        <ExpertProvider>
          {/* 스트림은 최고 관리자만 받는다(프레임이 모든 임대·VM 의 id 를 싣는다, F-38). 나머지는 폴링만 한다(#255). */}
          <RealtimeProvider enabled={user.role === 'superadmin'}>
            {user.role === 'user' ? (
              <UserShell user={user} onLogout={() => void logoutThen(onOut)} />
            ) : (
              <Shell user={user} onOut={onOut} />
            )}
          </RealtimeProvider>
        </ExpertProvider>
      </SessionProvider>
    </TooltipProvider>
  )
}

async function logoutThen(onOut: () => void) {
  try {
    await auth.logout()
  } catch {
    // 이미 만료됐을 수 있다. 어느 쪽이든 로그인 화면으로 돌아간다.
  }
  onOut()
}

function Shell({ user, onOut }: { user: User; onOut: () => void }) {
  const [query, setQuery] = useState('')
  const [sidebar, setSidebar] = useState(true)
  // 서랍의 열림은 넓은 화면의 접기와 별개다 — 접어 둔 사람의 서랍이 비면 안 된다.
  const [drawer, setDrawer] = useState(false)
  const { world, loading, error, refresh } = useWorld()

  const logout = () => logoutThen(onOut)

  return (
    <div className="flex h-dvh overflow-hidden">
      {/* 레일과 사이드바는 넓은 화면의 것이다. 좁은 화면에서는 같은 둘이 서랍에 들어간다. */}
      <div className="hidden h-full lg:flex">
        <Rail pending={world.review.length} />
        {sidebar && <Sidebar />}
      </div>
      <NavDrawer open={drawer} onOpenChange={setDrawer} pending={world.review.length} />

      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <Topbar
          user={user}
          search={<TopSearch world={world} query={query} onQuery={setQuery} />}
          pending={world.review.length}
          onToggleSidebar={() => setSidebar((v) => !v)}
          onOpenNav={() => setDrawer(true)}
          onLogout={logout}
        />

        <ScrollArea className="min-h-0 flex-1">
          <main className="mx-auto max-w-6xl px-6 pt-4 pb-12 max-sm:px-4">
            {error ? (
              <div className="pt-4">
                <Err error={error} />
              </div>
            ) : null}
            <WorldRefreshProvider refresh={refresh}>
              <Screens world={world} query={query} loading={loading} />
            </WorldRefreshProvider>
          </main>
        </ScrollArea>
      </div>
    </div>
  )
}

function SessionWait() {
  return <div className="grid h-full place-items-center text-sm text-muted-foreground">세션 확인 중</div>
}
